import { useState, useEffect } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { getExperienceThingsToKnow } from 'api/experiences'

function isEnglish(locale: string | undefined): boolean {
  return !locale || locale === 'en' || locale.startsWith('en-')
}

interface Result {
  items: Array<App.ExperienceMarkdownSplitted>
  isLoading: boolean
  isUsingInlineItems: boolean
}

function useThingsToKnow(
  experienceId: string,
  inlineItems: Array<App.ExperienceMarkdownSplitted>,
): Result {
  const locale = useAppSelector(state => state.geo.locale)
  const [items, setItems] = useState<Array<App.ExperienceMarkdownSplitted> | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(() => !isEnglish(locale))

  useEffect(() => {
    if (isEnglish(locale)) {
      return
    }

    setIsLoading(true)
    setItems(undefined)

    const controller = new AbortController()

    getExperienceThingsToKnow(experienceId, locale!, controller.signal)
      .then(result => {
        if (!controller.signal.aborted) {
          setItems(result)
          setIsLoading(false)
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setItems([])
          setIsLoading(false)
        }
      })

    return () => controller.abort()
  }, [experienceId, locale])

  if (isEnglish(locale)) {
    return { items: inlineItems, isLoading: false, isUsingInlineItems: true }
  }

  return { items: items ?? [], isLoading, isUsingInlineItems: false }
}

export default useThingsToKnow
